import {useEffect, useState} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {BookOpen, ChevronDown, ChevronRight, Plus, Trash2} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import {ATTRIBUTE_STATS, SPELLCASTING_TYPES} from "@/characters/model/Character.ts";
import {CharacterFormInput, newSpellGroup} from "@/characters/admin/form/CharacterFormSchema.ts";
import SelectInputField from "@/components/forms/SelectInputField.tsx";
import NumberInputField from "@/components/forms/NumberInputField.tsx";
import SpellGroupView from "@/characters/admin/form/SpellGroupView.tsx";
import MarkdownEditorField from "@/components/markdown/MarkdownEditorField.tsx";
import DeleteConfirm from "@/components/forms/DeleteConfirm.tsx";
import FormSection from "@/components/forms/FormSection.tsx";

export default function CharacterSpellsForm() {
    const {control, setValue} = useFormContext<CharacterFormInput>();
    const spellcasting = useWatch({control, name: "spellcasting"});
    const [descriptionOpen, setDescriptionOpen] = useState(false);

    useEffect(() => {
        if (!spellcasting) return;

        if (spellcasting.type === "Klasse" && spellcasting.spellGroups.some(group => !("level" in group))) {
            setValue("spellcasting.spellGroups", spellcasting.spellGroups.map((group, index) => ({
                level: index,
                slots: index === 0 ? 0 : 1,
                spells: group.spells,
            })), {shouldDirty: true, shouldValidate: true});
        }

        if (spellcasting.type === "Angeboren" && spellcasting.spellGroups.some(group => !("frequency" in group))) {
            setValue("spellcasting.spellGroups", spellcasting.spellGroups.map(group => ({
                frequency: "Beliebig",
                spells: group.spells,
            })), {shouldDirty: true, shouldValidate: true});
        }
    }, [setValue, spellcasting]);

    const addSpellcasting = () => setValue("spellcasting", {
        type: "Klasse",
        attribute: "cha",
        description: "",
        spellGroups: [newSpellGroup()],
    }, {shouldDirty: true, shouldValidate: true});

    const removeSpellcasting = () => setValue("spellcasting", undefined, {
        shouldDirty: true,
        shouldValidate: true,
    });

    if (!spellcasting) return <FormSection title="Zauberwirken"
                                           description="Grundwerte und verfügbare Zauber des Charakters.">
        <div className="grid min-h-72 place-content-center justify-items-center gap-4 rounded-lg border border-dashed p-6 text-center">
            <BookOpen className="size-12 text-muted-foreground"/>
            <div>
                <h3 className="font-semibold">Kein Zauberwirken eingerichtet</h3>
                <p className="mt-1 max-w-md text-sm text-muted-foreground">
                    Füge Zauberwerte und nach Grad oder Häufigkeit geordnete Zaubergruppen hinzu.
                </p>
            </div>
            <Button type="button" onClick={addSpellcasting}><Plus/> Zauberwirken hinzufügen</Button>
        </div>
    </FormSection>;

    return <FormSection title="Zauberwirken"
                        description="Grundwerte und verfügbare Zauber des Charakters."
                        actions={
        <DeleteConfirm title="Zauberwirken entfernen?"
                       description="Alle Zaubergruppen dieses Charakters werden aus dem Formular entfernt."
                       onConfirm={removeSpellcasting}>
            <Button type="button" variant="outline" size="sm" className="text-destructive hover:text-destructive">
                <Trash2/> Zauberwirken entfernen
            </Button>
        </DeleteConfirm>
    } contentClassName="space-y-4">
        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SelectInputField name="spellcasting.type" title="Art" options={SPELLCASTING_TYPES}/>
            <SelectInputField name="spellcasting.attribute" title="Zauberattribut"
                              options={ATTRIBUTE_STATS} nullable/>
            <NumberInputField name="spellcasting.saveDc" title="Rettungswurf-SG"/>
            <NumberInputField name="spellcasting.skill" title="Angriffsbonus"/>
        </section>
        <section className="border-t pt-3">
            <button type="button" className="flex w-full items-center gap-2 rounded-md text-left"
                    onClick={() => setDescriptionOpen(open => !open)}
                    aria-expanded={descriptionOpen}>
                {descriptionOpen ? <ChevronDown className="size-4"/> : <ChevronRight className="size-4"/>}
                <span className="font-medium">Beschreibung</span>
                {!descriptionOpen && <span className="min-w-0 grow truncate text-sm text-muted-foreground">
                    {descriptionSummary(spellcasting.description)}
                </span>}
                <span className="ml-auto text-xs text-muted-foreground">
                    {descriptionOpen ? "Schließen" : "Bearbeiten"}
                </span>
            </button>
            {descriptionOpen && <div className="mt-3">
                <MarkdownEditorField<CharacterFormInput> name="spellcasting.description" title="Inhalt"/>
            </div>}
        </section>
        <SpellGroupView/>
    </FormSection>;
}

function descriptionSummary(description?: string) {
    if (!description?.trim()) return "Keine Beschreibung";
    return description.replace(/\s+/g, " ").trim();
}
